import React, { useState } from 'react';
import { Platform, StyleSheet, Text, TextInput, View } from 'react-native';
import { CommonActions, useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { ContentView } from '../components/ContentView';
import usePasswordToggle from '../components/usePasswordToggle';
import { rootNavigationRef } from '../navigation/rootNavigationRef';
import { setEmail, signout, userTokenSelector } from '../redux/appSlice';
import { useResetPasswordMutation } from '../services/abbiWayApi';
import { reportServerError } from '../utils/reportServerError';
import { theme } from '../theme';
export const PasswordChangeScreen = ({ route, }) => {
    const hash = route.params?.hash;
    const emailFromLink = route.params?.email;
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const dispatch = useDispatch();
    const navigation = useNavigation();
    const userToken = useSelector(userTokenSelector);
    const [resetPassword, { isLoading }] = useResetPasswordMutation();
    const eyeColor = theme.colors.textLinkStrong;
    const [showPassword, PasswordToggleIcon] = usePasswordToggle(password.length !== 0, eyeColor, theme.colors.controlInactive);
    const [showConfirm, ConfirmToggleIcon] = usePasswordToggle(confirmPassword.length !== 0, eyeColor, theme.colors.controlInactive);
    const goToSignIn = () => {
        if (typeof emailFromLink === 'string' && emailFromLink.trim()) {
            dispatch(setEmail(emailFromLink.trim()));
        }
        if (!userToken) {
            navigation.dispatch(CommonActions.reset({
                index: 0,
                routes: [{ name: 'Signin' }],
            }));
        }
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.history.replaceState({}, document.title, `${window.location.origin}/`);
        }
        dispatch(signout());
        if (userToken) {
            requestAnimationFrame(() => {
                if (rootNavigationRef.isReady()) {
                    try {
                        rootNavigationRef.dispatch(CommonActions.reset({
                            index: 0,
                            routes: [{ name: 'Signin' }],
                        }));
                    }
                    catch {
                        /* root may not accept this shape if navigators differ */
                    }
                }
            });
        }
    };
    const submit = async () => {
        if (!hash) {
            return;
        }
        if (password.length < 1) {
            return;
        }
        if (password !== confirmPassword) {
            return;
        }
        try {
            await resetPassword({ hash, password }).unwrap();
            Alert('Password updated', 'You can sign in with your new password.', [{ text: 'OK', style: 'default', onPress: goToSignIn }]);
        }
        catch (err) {
            reportServerError(err);
        }
    };
    if (!hash) {
        return (<ContentView>
        <View style={styles.container}>
          <Text style={styles.errorText}>
            This reset link is missing required information. Open the link from
            your email, or request a new reset from the sign-in screen.
          </Text>
        </View>
      </ContentView>);
    }
    const passwordsMatch = password.length > 0 && password === confirmPassword;
    const canSubmit = passwordsMatch && !isLoading;
    return (<ContentView>
      <View style={styles.container}>
        <Text style={styles.hint}>
          Choose a new password for your account.
        </Text>
        <View style={styles.row}>
          <TextInput secureTextEntry={!showPassword} style={styles.input} placeholder="New password" placeholderTextColor={theme.colors.textCaption} value={password} onChangeText={setPassword} autoCapitalize="none"/>
          <PasswordToggleIcon />
        </View>
        <View style={styles.row}>
          <TextInput secureTextEntry={!showConfirm} style={styles.input} placeholder="Confirm new password" placeholderTextColor={theme.colors.textCaption} value={confirmPassword} onChangeText={setConfirmPassword} autoCapitalize="none"/>
          <ConfirmToggleIcon />
        </View>
        {confirmPassword.length > 0 && !passwordsMatch ? (<Text style={styles.errorText}>Passwords do not match.</Text>) : null}
        <View style={styles.button}>
          <Button title={isLoading ? 'Saving...' : 'Update password'} disabled={!canSubmit} onPress={submit}/>
        </View>
      </View>
    </ContentView>);
};
const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 24,
        paddingTop: 24,
    },
    hint: {
        fontSize: 16,
        marginBottom: 16,
        color: theme.colors.textTitle,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
    },
    input: {
        flex: 1,
        fontSize: 16,
        paddingHorizontal: 10,
        height: 44,
        borderWidth: 1,
        borderRadius: 8,
        backgroundColor: theme.colors.surfaceElevated,
    },
    errorText: {
        fontSize: 15,
        color: theme.colors.dangerStrong,
        marginBottom: 12,
    },
    button: {
        marginTop: 16,
    },
});
